import * as React from 'react';

import ServiceOptions from './ServiceOptions';
import Discounts, { DiscountProps } from './Discounts';
import Notes from './Notes';
import Amenities from './Amenities';
import Links, { VenueLinksProps } from './Links';
import Availability from './Availability';
import { ServiceProps } from '../service-options-modal/Services';
import PackagesSection from './packages';
import { PackagesProps } from './packages/Packages';
import UpcomingEvents, { EventProps } from './upcoming-events/';
import SocialTables, { SocialTablesData } from './social-tables';
import { useEffect, useState } from 'react';
import { Base64 } from 'js-base64';

import axios from 'axios';
import { CSN_VENUE_PROFILE_PROD_URL, CSN_VENUE_PROFILE_URL } from '../../constants/config';
import * as querystring from 'query-string';
import { AnalyticsEvent, logEvent } from '@weddingspot/ws-analytics';
import {
    GuestRooms,
    GuestRoomsData,
    FeatureFlagContext,
    FLAG_ROOM_BLOCK_RECOMMENDATIONS_SHOWN_ON_PROFILE
} from '@weddingspot/ws-core-components';
import { CSNGeographicPointFilter, CSNQueryParserResult, CSNServiceApi } from '@weddingspot/ws-api-client';
import {CSNVenueSearchInput} from "@weddingspot/ws-api-client/src";


const styles = require('./VenueAdditionalDetails.scss');

export interface IProps extends VenueLinksProps, PackagesProps {
    venueId?: number;
    venueCSNId?: string;
    onSleepingRoomsSelect: () => void;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    // Component-level props
    onAccordionExpanded?: (name: string) => void;
    areAccordionsOpen: boolean;
    // Venue notes
    venueNotes: string;

    // Amenities/special restrictions
    amenities: {
        id: number;
        name: string;
    }[];
    restrictions: {
        id: number;
        name: string;
    }[];

    // Availablility
    calendarRef?: React.Ref<HTMLDivElement>;

    // Service Options
    ceremonies: ServiceProps[];
    receptions: ServiceProps[];
    rds: ServiceProps[];
    availability: { [date: string]: number } | null;
    availabilityLastUpdated?: string;
    upcomingEvents: EventProps[];

    // Discounts
    discounts: DiscountProps[];

    // Guest Rooms
    showSleepingRoomsOnVenueProfile?: boolean;
    hideGuestRooms?: boolean;

    onClickInquire?: () => void;
}

export interface IState {
    isShown: boolean;
}

interface CSNVenueData extends GuestRoomsData, SocialTablesData {
    basicProfile: GuestRoomsData['basicProfile'] & SocialTablesData['basicProfile'];
}

const VenueAdditionalDetails = (props: IProps) => {
    const { flagEnabled } = React.useContext(FeatureFlagContext);
    const showRoomBlockRecommendations = flagEnabled(FLAG_ROOM_BLOCK_RECOMMENDATIONS_SHOWN_ON_PROFILE);
        
    const [csnVenueData, setCSNVenueData] = useState<CSNVenueData>();

    const [exploreHotelsURL, setExploreHotelsURL] = useState('');
    const [searchResults, setSearchResults] = React.useState<any[]>([]);
    const [profilesResults, setProfilesResults] = React.useState<any[]>([]);

    useEffect(
        () => {
            if (!props.hideGuestRooms && props.address) {
                (async () => {
                    const queryParserRes = await CSNServiceApi.queryParser({
                        queryString: `${props.address}, ${props.city}, ${props.state} ${props.zipCode}`,
                    });
                    const geoFilter = getGeoFilter(queryParserRes);
                    const searchState = {
                        q: queryParserRes.formattedSearchText,
                        f: geoFilter,
                        b: queryParserRes.boundingBoxFilter.boundingBox,
                    };
                    const redirectUrl: string = `/hotels/search/?state=${Base64.btoa(JSON.stringify(searchState))}`;
                    setExploreHotelsURL(redirectUrl);
                    if (showRoomBlockRecommendations) {
                        const requestPayload: CSNVenueSearchInput = {
                            query: queryParserRes.formattedSearchText,
                            geoFilter: geoFilter,
                            boundingBox: queryParserRes.boundingBoxFilter.boundingBox,
                            radius: 60,
                            pageFilter: {
                                from: 0,
                                size: 4,
                            },
                        };
                        const searchRes = await CSNServiceApi.venuesSearch(requestPayload);
                        const profilesRes = await CSNServiceApi.getVenueProfiles({
                            venueIds: searchRes.searchResult.venueList.map((v) => v.id)
                        });
                        setSearchResults(searchRes.searchResult.venueList);
                        setProfilesResults(profilesRes);                        
                    }
                })();
            }
        },
        []
    );

    useEffect(() => {
        // fetch data after page is loaded to avoid penalties from search engines
        if (!!props.venueCSNId && document.readyState === 'complete') {
            // we can override w\ use_prod qp to force prod url
            const search = window.location.search;
            const useProd = querystring.parse(search).use_prod === '1';
            const url = useProd ? CSN_VENUE_PROFILE_PROD_URL : CSN_VENUE_PROFILE_URL;

            axios.post<[CSNVenueData]>(url, [props.venueCSNId]).then(({ data }) => {
                if (data.length === 1) {
                    const singleVenueData: CSNVenueData = data[0];
                    setCSNVenueData(singleVenueData);
                    logEvent(AnalyticsEvent.EVTNM_LOADED_HOTEL_ROOMS_SECTION, { Source: 'Wedding venue profile' });
                }
            });
        }
    }, [typeof window !== 'undefined' && document.readyState]);

    const getGeoFilter = (obj: CSNQueryParserResult): CSNGeographicPointFilter => {
        /*
        CSNQueryParser checking if a search term matches a pre-defined GeoJSON polygon stored in Elasticsearch.
        if the Google Suggestion geographic type matches one of the polygon types(
            * US states,
            * Canadian provinces,
            * the Caribbean
        ),
        CSNQueryParser provides 'POLYGON' filter
        */
        const polygon = (obj?.additionalFilters || []).find((i) => i.type === 'POLYGON');
        if (polygon) {
            return polygon;
        }
        return {
            ...obj.geographicPointFilter,
            maximum: 25,
            measurementUnit: 'MILES',
        };
    };

    const handleAccordionExpanded = (name: string) => {
        if (props.onAccordionExpanded) {
            props.onAccordionExpanded(name);
        }
    };

    const hasLinks = !!props.websiteUrl || !!props.facebookUrl || !!props.instagramUrl || !!props.pinterestUrl;

    return (
        <div className='VenuePage--additional-details-wrapper'>
            <div className={styles.hiddenMobile}>
                <ServiceOptions
                    ceremonies={props.ceremonies}
                    receptions={props.receptions}
                    rds={props.rds}
                    isShown={false}
                    onSectionExpanded={() => handleAccordionExpanded('Sevice Options')}
                    onClickInquire={props.onClickInquire}
                />
                <div className='VenuePage--additional-details-separator' />
            </div>

            {props.discounts.length > 0 && (
                <div>
                    <Discounts discounts={props.discounts} isShown={props.areAccordionsOpen} />
                    <div className='VenuePage--additional-details-separator' />
                </div>
            )}

            {!!props.venueNotes && (
                <div>
                    <Notes venueNotes={props.venueNotes} isShown={props.areAccordionsOpen} />
                    <div className='VenuePage--additional-details-separator' />
                </div>
            )}
            {props.amenities.length > 0 && (
                <div className={styles.hiddenMobile}>
                    <Amenities
                        amenities={props.amenities}
                        restrictions={props.restrictions}
                        showAmenities={true}
                        showRestrictions={true}
                        onSectionExpanded={() => handleAccordionExpanded('Amenities & Restrictions')}
                        isShown={props.areAccordionsOpen}
                    />
                    <div className='VenuePage--additional-details-separator' />
                </div>
            )}
            <div className={styles.visibleMobile}>
                {props.amenities.length > 0 && (
                    <div>
                        <Amenities
                            amenities={props.amenities}
                            restrictions={props.restrictions}
                            showAmenities={true}
                            showRestrictions={false}
                            onSectionExpanded={() => handleAccordionExpanded('Amenities')}
                            isShown={props.areAccordionsOpen}
                        />
                        <div className='VenuePage--additional-details-separator' />
                    </div>
                )}
                {props.restrictions.length > 0 && (
                    <div>
                        <Amenities
                            amenities={props.amenities}
                            restrictions={props.restrictions}
                            showAmenities={false}
                            showRestrictions={true}
                            onSectionExpanded={() => handleAccordionExpanded('Restrictions')}
                            isShown={props.areAccordionsOpen}
                        />
                        <div className='VenuePage--additional-details-separator' />
                    </div>
                )}
            </div>

            {!props.hideGuestRooms && props.showSleepingRoomsOnVenueProfile && (
                <GuestRooms
                    csnVenueData={csnVenueData}
                    onSectionExpanded={() => handleAccordionExpanded('Guest Rooms')}
                    onClick={props.onSleepingRoomsSelect}
                    exploreHotelsUrl={exploreHotelsURL}
                    hotelsList={searchResults}
                    hotelsProfiles={profilesResults}
                    showRecommendations={showRoomBlockRecommendations}
                />
            )}

            <SocialTables
                venueId={props.venueId}
                csnVenueData={csnVenueData}
                onSectionExpanded={(name: string) => handleAccordionExpanded(name)}
            />

            {hasLinks && (
                <Links
                    websiteUrl={props.websiteUrl}
                    onWebsiteClick={props.onWebsiteClick}
                    facebookUrl={props.facebookUrl}
                    instagramUrl={props.instagramUrl}
                    pinterestUrl={props.pinterestUrl}
                    onSocialClick={props.onSocialClick}
                    onSectionExpanded={() => handleAccordionExpanded('Links')}
                    isShown={false}
                />
            )}
            {hasLinks && props.availability !== null && <div className='VenuePage--additional-details-separator' />}
            {props.availability !== null && (
                <Availability
                    calendarRef={props.calendarRef}
                    availability={props.availability}
                    lastUpdatedDate={props.availabilityLastUpdated}
                    onSectionExpanded={() => handleAccordionExpanded('Availability')}
                    isShown={false}
                />
            )}
            {props.packages.length > 0 && <div className='VenuePage--additional-details-separator' />}
            {props.packages.length > 0 && (
                <PackagesSection packages={props.packages} onClickPackageInquire={props.onClickPackageInquire} isShown={false} />
            )}

            {/* upcoming events section */}
            {props.upcomingEvents.length > 0 && (
                <div>
                    <div className='VenuePage--additional-details-separator' />
                    <UpcomingEvents events={props.upcomingEvents} isShown={false} />
                </div>
            )}
            {/* upcoming events section */}
        </div>
    );
};

export default VenueAdditionalDetails;
